import { useEffect, useRef, useState } from 'react'
import { ApiError, useInitializeModule, useRunModuleSequence } from '@ctcells/api'
import type { ModuleSnapshot } from '@ctcells/contract'
import { REQUEST_TIMEOUT_MS } from '@ctcells/core'
import { errorMessage, t, type MessageKey } from '@ctcells/i18n'

import { AddModal } from '../atoms/AddModal.tsx'
import { Button } from '../atoms/Button.tsx'
import { Checkbox } from '../atoms/Checkbox.tsx'
import type { ActionGate, ScreenAccess } from '../screens/access.ts'
import { FormSection, ModalFooter } from '../screens/formSections.tsx'
import { moduleWriteGate } from '../screens/modules/moduleInitializeGate.ts'

/**
 * 정지 풀기 결과의 «문 닫기 미처리» 줄 아래 [문 닫고 원점 복귀] — 2026-10-29 실사례(감사 4121).
 *
 * 정지 풀기(`POST /safety/stops/release`)가 `door_open` 항목에 `close_door` 를 보냈는데 모듈이 409 `MODULE_ABORTED`
 * (취소로 멈춤)로 거절했다. 문이 열려 있으면 초기화도 막힌다(모듈 `MODULE_HANDOFF_DOOR_OPEN` · BR-145 고리).
 * 모듈은 지령 개방 구간에도 «문 시퀀스»는 받는다(module-core `commands.py` 지령 개방 `excepts`) — 팀장 실측 2026-10-28:
 * ABORTED 에서 시퀀스 중계 `close_door` 로 문이 닫히고, 이어 초기화 중계로 IDLE 이 된다.
 *
 * | 걸음 | 요청 | 넘어가는 조건 |
 * | --- | --- | --- |
 * | ① 문 닫기 | `POST /modules/{id}/control/sequences` `{sequence:'close_door', params:{door}}` · 열린 면마다 | 202 |
 * | ② 문 닫힘 확인 | (요청 없음) 구독이 갱신한 `GET /modules` 캐시 | `exit_blockers` 에 `handoff_door_open` 이 빠지고 실행이 없다 |
 * | ③ 원점 복귀 | `POST /modules/{id}/control/initialize` | 202 → 정지 목록 다시 읽기 |
 *
 * 🔴 ②는 폴링이 아니다 — 모듈 상태 구독의 무효화로 바뀐 캐시를 볼 뿐이다. 판독이 안 오면 상한(`REQUEST_TIMEOUT_MS.prepare`)에서 멈춘다.
 * 🔴 정지 풀기는 다시 보내지 않는다(DP-02) — 사람이 [현장 확인 후 정지 풀기]를 다시 누른다.
 * 🔴 두 요청 본문에 사유 칸이 없다(`ModuleSequenceRunRequest` · `InitializeModuleControlBody` 실서버 openapi) — 받을 곳 없는 사유를 묻지 않는다.
 * 🔴 문 면은 서버 값(`interlock.doors[].state === 'open'`)에서만 고른다. 모르면 `params` 를 비워 모듈 스키마 기본(`rear`)에 맡긴다.
 */
const ABORTED_DOOR_CODE = 'MODULE_ABORTED'
const DOOR_BLOCKER = 'handoff_door_open'
/** 단추를 세우는 모듈 상태(스냅숏 `state`) — 취소로 멈춤 */
const ABORTED_STATE = 'ABORTED'

type Step = 'closeDoor' | 'waitDoor' | 'initialize'
type Run =
  | { phase: 'idle' }
  | { phase: 'running'; step: Step }
  | { phase: 'failed'; step: Step; message: string; code?: string | undefined }
  | { phase: 'done' }

const STEP_LABEL: Record<Step, MessageKey> = {
  closeDoor: 'stopsRelease.recover.step.closeDoor',
  waitDoor: 'stopsRelease.recover.step.waitDoor',
  initialize: 'stopsRelease.recover.step.initialize',
}

/** 열린 문 면(서버 판독) — 없거나 모르면 빈 배열 */
function openFaces(module: ModuleSnapshot | undefined): string[] {
  return (module?.interlock?.doors ?? []).flatMap((door) =>
    door.state === 'open' && typeof door.face === 'string' ? [door.face] : [],
  )
}

/** 문이 닫혔다는 판독 — 원점 복귀를 막는 까닭에 문이 없고(칸을 모르면 아니다) 도는 실행도 없다 */
function doorClosed(module: ModuleSnapshot | undefined): boolean {
  const blockers = module?.exit_blockers
  return (
    Array.isArray(blockers) &&
    !blockers.includes(DOOR_BLOCKER) &&
    (module?.current_execution === null || module?.current_execution === undefined)
  )
}

function failureCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined
  return error.error_code ?? (error.status === undefined ? undefined : String(error.status))
}

export interface StopsAbortedDoorRecoverProps {
  access: ScreenAccess
  /** 보낸 정지 항목의 종류 — `door_open` = 정지 풀기가 그 모듈에 `close_door` 를 보낸 걸음 */
  kind: string | undefined
  /** 결과 줄(`StopsReleaseResult.items[]`)의 `done` · `error_code` 그대로 — said 문장은 읽지 않는다 */
  done: boolean
  errorCode: string | null | undefined
  moduleId: string
  /** 그 모듈 스냅숏(`GET /modules` 캐시 · 구독 무효화) — 없으면 문 면을 모르고 닫힘 판독도 오지 않는다 */
  module: ModuleSnapshot | undefined
  /** 끝난 뒤 정지 목록을 다시 읽는다(풀기는 하지 않는다) */
  onFinished: () => Promise<unknown>
}

/** 그 줄이 «취소로 멈춘 모듈의 문 닫기 거절»이 아니면 아무것도 그리지 않는다 */
export function StopsAbortedDoorRecover(props: StopsAbortedDoorRecoverProps) {
  return !props.done && props.kind === 'door_open' && props.errorCode === ABORTED_DOOR_CODE ? (
    <AbortedDoorRecover {...props} />
  ) : null
}

function AbortedDoorRecover({
  access,
  moduleId,
  module,
  onFinished,
}: StopsAbortedDoorRecoverProps) {
  const sequence = useRunModuleSequence()
  const initialize = useInitializeModule()
  const [open, setOpen] = useState(false)
  const [checked, setChecked] = useState(false)
  const [run, setRun] = useState<Run>({ phase: 'idle' })
  const sending = useRef(false)
  const label = t('stopsRelease.recover.action')
  const busy = run.phase === 'running'

  const gate: ActionGate = moduleWriteGate({
    access,
    operations: null,
    operationIds: [],
    controlOwner: module?.control_owner ?? '',
    sending: busy,
  })
  const confirmGate: ActionGate = gate.disabled
    ? gate
    : checked
      ? { disabled: false }
      : { disabled: true, disabledReason: t('reason.physicalChecksRequired') }

  /*
   * ② 닫힘 판독 — 구독이 바꾼 캐시를 ref 로 넘겨 받는다(렌더 중 ref 를 쓰지 않는다 · 효과 안에서 setState 하지 않는다).
   *    기다리는 쪽은 `start` 의 promise 하나다.
   */
  const closed = doorClosed(module)
  const closedRef = useRef(closed)
  const waiter = useRef<(() => void) | null>(null)
  useEffect(() => {
    closedRef.current = closed
    if (closed) waiter.current?.()
  }, [closed])
  useEffect(
    () => () => {
      waiter.current = null
    },
    [],
  )
  const waitClosed = () =>
    new Promise<boolean>((resolve) => {
      if (closedRef.current) {
        resolve(true)
        return
      }
      const timer = setTimeout(() => {
        waiter.current = null
        resolve(false)
      }, REQUEST_TIMEOUT_MS.prepare)
      waiter.current = () => {
        clearTimeout(timer)
        waiter.current = null
        resolve(true)
      }
    })

  const start = async () => {
    if (confirmGate.disabled || sending.current) return
    sending.current = true
    setOpen(false)
    setChecked(false)
    const failAt = (step: Step, error: unknown, message?: string) =>
      setRun({
        phase: 'failed',
        step,
        message: message ?? errorMessage(error as ApiError),
        code: failureCode(error),
      })
    try {
      // ① 문 닫기 — 열린 면마다(모르면 모듈 스키마 기본)
      setRun({ phase: 'running', step: 'closeDoor' })
      const faces = openFaces(module)
      try {
        for (const face of faces.length === 0 ? [undefined] : faces)
          await sequence.mutateAsync({
            moduleId,
            sequence: 'close_door',
            params: face === undefined ? {} : { door: face },
          })
      } catch (error) {
        failAt('closeDoor', error)
        return
      }
      // ② 문 닫힘 판독
      setRun({ phase: 'running', step: 'waitDoor' })
      if (!(await waitClosed())) {
        failAt('waitDoor', undefined, t('stopsRelease.recover.doorTimeout'))
        return
      }
      // ③ 원점 복귀
      setRun({ phase: 'running', step: 'initialize' })
      try {
        await initialize.mutateAsync({ moduleId })
      } catch (error) {
        failAt('initialize', error)
        return
      }
      await onFinished()
      setRun({ phase: 'done' })
    } finally {
      sending.current = false
    }
  }

  return (
    <div className="stops-release__recover">
      {/* 🔴 단추는 그 모듈이 «지금» ABORTED 일 때만 — 「지금:」 줄과 같은 스냅숏. 원점 복귀 뒤(IDLE)에는 걸음 결과만 남는다 */}
      {module?.state === ABORTED_STATE ? (
        <Button
          aria-label={label}
          size="pill"
          {...gate}
          onClick={() => {
            setChecked(false)
            setOpen(true)
          }}
        >
          {label}
        </Button>
      ) : null}
      {run.phase === 'running' ? (
        <p className="small muted" role="status">
          {t('stopsRelease.recover.running', { step: t(STEP_LABEL[run.step]) })}
        </p>
      ) : run.phase === 'failed' ? (
        <p className="vissue" role="alert">
          {t('stopsRelease.recover.failed', {
            step: t(STEP_LABEL[run.step]),
            message: run.message,
          })}
          {run.code === undefined ? null : ` (${run.code})`}
        </p>
      ) : run.phase === 'done' ? (
        <p className="small" role="status">
          {t('stopsRelease.recover.done')}
        </p>
      ) : null}
      {open ? (
        <AddModal busy={busy} onClose={() => setOpen(false)} size="sm" title={label}>
          <FormSection>
            <p className="deny">{t('stopsRelease.recover.confirmBody', { module: moduleId })}</p>
            <Checkbox
              label={t('stopsRelease.recover.check')}
              checked={checked}
              onChange={setChecked}
            />
          </FormSection>
          <ModalFooter>
            <Button aria-label={t('action.cancel')} size="pill" onClick={() => setOpen(false)}>
              {t('action.cancel')}
            </Button>
            <Button
              aria-label={`${label} ${t('action.confirm')}`}
              size="pill"
              variant="primary"
              {...confirmGate}
              onClick={() => {
                void start()
              }}
            >
              {label}
            </Button>
          </ModalFooter>
        </AddModal>
      ) : null}
    </div>
  )
}
